import { OnboardingChapter } from '../enums/OnboardingChapter'
import { EventType, type ItrEvent } from '../types/GameTypes'
import { GroupTutorialStatus, type GameOnboardingSessionDoc, type GroupTutorialSummary } from '../types/GroupTutorial'
import type { User } from '../types/User'
import { ONE_MINUTE_MS } from './Consts'

/** The game moves a stuck team on by itself once the onboarding has run this long (`StartupProperties`). */
export const GROUP_TUTORIAL_PROCEED_MS = 6 * ONE_MINUTE_MS // 6 min

/** The game writes ISO strings, C# "u" strings (`2026-06-12 08:11:40Z`) and Firestore Timestamps into the same fields. */
export const parseGameTimestampToMillis = (value: unknown): number | undefined => {
  if (!value) return undefined

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.getTime()
  }

  if (typeof value === 'string') {
    const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T')).getTime()
    return Number.isNaN(parsed) ? undefined : parsed
  }

  // Duck-typed rather than importing a Firestore SDK, so this stays usable from both browsers and functions.
  const timestamp = value as { toDate?: () => Date; seconds?: number; _seconds?: number }
  if (typeof timestamp.toDate === 'function') {
    return parseGameTimestampToMillis(timestamp.toDate())
  }

  const seconds = timestamp.seconds ?? timestamp._seconds
  return typeof seconds === 'number' ? seconds * 1000 : undefined
}

const toGameTimestampIso = (value: unknown): string | undefined => {
  const millis = parseGameTimestampToMillis(value)
  return millis === undefined ? undefined : new Date(millis).toISOString()
}

/** Structural so the frontend's own user shape (chapters typed as plain strings) fits without a cast. */
type TutorialProgressUser = Pick<User, 'tutorialCompletedAt'> & { completedOnboardingSteps?: string[] }

/** The game's own word that the solo tutorial was played to the end — not merely opened. */
export const hasCompletedSoloTutorial = (user?: Pick<User, 'tutorialCompletedAt'>): boolean =>
  parseGameTimestampToMillis(user?.tutorialCompletedAt) !== undefined

/** Done when the game reported a completion, or when the chapter was already stored against the account. */
export const isTutorialOnboardingStepCompleted = (user?: TutorialProgressUser): boolean =>
  hasCompletedSoloTutorial(user) || (user?.completedOnboardingSteps || []).includes(OnboardingChapter.Tutorial)

/**
 * Events a player can only produce by doing something. Auto-generated events (GameStateChanged, Paralyzed,
 * ExitedBase, Inactivity, etc.) fire for all assigned players regardless of whether they actually connected.
 */
export const PLAYER_ACTION_EVENT_TYPES = new Set<EventType>([
  EventType.Circumplex,
  EventType.Speak,
  EventType.ResourceShared,
  EventType.AbilityShared,
  EventType.FireShared,
  EventType.PathSelected,
  EventType.UnlockedRobot,
  EventType.PlacedFlag,
  EventType.UsedMap,
  EventType.MovedFlag,
  EventType.UridiumDeposited,
  EventType.AbilityUsed,
  EventType.OpenedDoor,
  EventType.CalledDrone,
  EventType.InterruptedDrone,
  EventType.UridiumHarvested,
  EventType.PickedUpItem,
  EventType.DroppedItem,
  EventType.ActivatedItem,
  EventType.ReflectionQuestionCompleted,
  EventType.InitiatedRobotUnlock,
  EventType.SpentTimeDronePuzzle,
  EventType.OpenSquadWindow,
  EventType.CloseSquadWindow
])

// The game hands every ASSIGNED player a starting item the instant a loop or the group tutorial begins, even players who never connected.
const LOADOUT_ANCHOR_EVENT_TYPES = new Set<EventType>([EventType.StartInGame, EventType.OnboardingStarted])

const isPlayerActionEvent = (event: ItrEvent): boolean => PLAYER_ACTION_EVENT_TYPES.has(event.type as EventType)

const isStartupLoadoutPickup = (event: ItrEvent, loadoutTimestamps: Set<number>): boolean =>
  (event.type as EventType) === EventType.PickedUpItem && loadoutTimestamps.has(event.timeStamp)

/**
 * Who actually did something in a loop's or the group tutorial's events: whoever started it plus every real player
 * action, minus the startup loadout broadcast to everyone. Someone who only walks around emits nothing trackable.
 */
export const getPlayedUserIds = (events: ItrEvent[]): string[] => {
  const loadoutTimestamps = new Set(
    events.filter((event) => LOADOUT_ANCHOR_EVENT_TYPES.has(event.type as EventType)).map((event) => event.timeStamp)
  )
  const played = new Set<string>()
  for (const event of events) {
    if (!event.userId) continue
    if (LOADOUT_ANCHOR_EVENT_TYPES.has(event.type as EventType)) {
      played.add(event.userId)
      continue
    }
    if (!isPlayerActionEvent(event)) continue
    if (isStartupLoadoutPickup(event, loadoutTimestamps)) continue
    played.add(event.userId)
  }
  return Array.from(played)
}

export const buildGroupTutorialSummary = ({
  onboardingSession,
  events,
  namesByUserId,
  nowMs
}: {
  onboardingSession: GameOnboardingSessionDoc
  events: ItrEvent[]
  namesByUserId: Map<string, string>
  nowMs: number
}): GroupTutorialSummary => {
  const startedAtMs = parseGameTimestampToMillis(onboardingSession.startedAt)
  const completedAtMs = parseGameTimestampToMillis(onboardingSession.completedAt)
  const status = completedAtMs === undefined ? GroupTutorialStatus.IN_PROGRESS : GroupTutorialStatus.COMPLETED
  const elapsedMs = startedAtMs === undefined ? undefined : (completedAtMs ?? nowMs) - startedAtMs
  const participantUserIds = getPlayedUserIds(events)
  const participantNames = participantUserIds.map((userId) => namesByUserId.get(userId) ?? userId)

  return {
    sessionId: onboardingSession.sessionId || onboardingSession.id,
    status,
    startedAt: toGameTimestampIso(onboardingSession.startedAt),
    completedAt: toGameTimestampIso(onboardingSession.completedAt),
    minutes: elapsedMs === undefined ? null : Math.max(1, Math.ceil(elapsedMs / ONE_MINUTE_MS)),
    participantNames,
    participantCount: participantNames.length,
    participantUserIds,
    isOverdue:
      status === GroupTutorialStatus.IN_PROGRESS && elapsedMs !== undefined && elapsedMs > GROUP_TUTORIAL_PROCEED_MS
  }
}
